import { useState, useEffect, useRef, useCallback } from 'preact/hooks';
import { useOddball } from '../midi-context';
import { OddballCC } from '../../src/types';
import type { CCUpdateEvent, OddballEvent, RollStartEvent, RollEndEvent, RollDiagnostics } from '../../src/types';

// ── Types ──────────────────────────────────────────────────────────────

interface DiagSnapshot {
  t: number;
  rolling: boolean;
  backoff: boolean;
  impactGrace: boolean;
  energyFast: number;
  energySlow: number;
  omegaFast: number;
  omegaSlow: number;
  spinEma: number;
  oscillationFreq: number;
  dropTimerMs: number;
  lowFreqCount: number;
}

interface RollCapture {
  index: number;
  startTime: number;
  orientSamples: { x: number; y: number; z: number; t: number }[];
  spinSamples: { value: number; t: number }[];
  ccSamples: { cc: number; value: number; t: number }[];
  notes: { type: string; velocity: number; t: number }[];
  rollDetected: boolean;
  detectedAtMs: number | null;
  rollEndedAtMs: number | null;
  durationMs: number;
  diagSnapshots: DiagSnapshot[];
  rollStartCount: number;
  rollEndCount: number;
}

type Phase = 'idle' | 'waiting' | 'recording' | 'review';

const SPARKLINE_W = 120;
const SPARKLINE_H = 32;

// ── Sparkline ──────────────────────────────────────────────────────────

function Sparkline({ data, color, width = SPARKLINE_W, height = SPARKLINE_H }: {
  data: number[];
  color: string;
  width?: number;
  height?: number;
}) {
  if (data.length < 2) return <div style={{ width, height, background: '#1a1a1a', borderRadius: 4 }} />;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const points = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((v - min) / range) * (height - 4) - 2;
    return `${x},${y}`;
  }).join(' ');

  return (
    <svg width={width} height={height} style={{ display: 'block', background: '#1a1a1a', borderRadius: 4 }}>
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" />
    </svg>
  );
}

/** Boolean timeline: colored bar per sample */
function BoolTimeline({ data, color, label, width = 240, height = 10 }: {
  data: boolean[];
  color: string;
  label: string;
  width?: number;
  height?: number;
}) {
  if (data.length === 0) return null;
  const pxPerSample = Math.max(1, width / data.length);
  return (
    <div style={{ marginBottom: 4 }}>
      <div style={{ fontSize: '0.55rem', color: '#666', marginBottom: 1 }}>{label}</div>
      <svg width={width} height={height} style={{ display: 'block', background: '#111', borderRadius: 2 }}>
        {data.map((v, i) => v ? (
          <rect key={i} x={i * pxPerSample} y={0} width={Math.max(1, pxPerSample)} height={height} fill={color} />
        ) : null)}
      </svg>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────

export function RollCalibrate() {
  const { parser, rollDetector } = useOddball();
  const [phase, setPhase] = useState<Phase>('idle');
  const [rollIndex, setRollIndex] = useState(0);
  const [rolls, setRolls] = useState<RollCapture[]>([]);
  const [totalRolls, setTotalRolls] = useState(10);

  // Live sparkline data for current recording (updated via rAF)
  const [liveOrient, setLiveOrient] = useState<{ x: number[]; y: number[]; z: number[] }>({ x: [], y: [], z: [] });
  const [liveSpin, setLiveSpin] = useState<number[]>([]);

  // Refs for recording state (avoid re-renders during capture)
  const phaseRef = useRef<Phase>('idle');
  const rollIndexRef = useRef(0);
  const captureRef = useRef<RollCapture | null>(null);
  const rollsRef = useRef<RollCapture[]>([]);
  const totalRollsRef = useRef(10);
  const rafRef = useRef(0);
  const dirtyRef = useRef(false);
  const redoingRef = useRef(false);

  // Auto-focus button ref
  const actionBtnRef = useRef<HTMLButtonElement>(null);

  // Live display accumulators
  const liveXRef = useRef<number[]>([]);
  const liveYRef = useRef<number[]>([]);
  const liveZRef = useRef<number[]>([]);
  const liveSpinRef = useRef<number[]>([]);

  // Sync phase to ref
  useEffect(() => { phaseRef.current = phase; }, [phase]);
  useEffect(() => { rollIndexRef.current = rollIndex; }, [rollIndex]);
  useEffect(() => { totalRollsRef.current = totalRolls; }, [totalRolls]);

  // Auto-focus the action button when phase changes to waiting or recording
  useEffect(() => {
    if (phase === 'waiting' || phase === 'recording') {
      requestAnimationFrame(() => actionBtnRef.current?.focus());
    }
  }, [phase]);

  // ── Start a new capture segment ──────────────────────────────────────

  const startCapture = useCallback((idx: number) => {
    const now = performance.now();
    captureRef.current = {
      index: idx,
      startTime: now,
      orientSamples: [],
      spinSamples: [],
      ccSamples: [],
      notes: [],
      rollDetected: false,
      detectedAtMs: null,
      rollEndedAtMs: null,
      durationMs: 0,
      diagSnapshots: [],
      rollStartCount: 0,
      rollEndCount: 0,
    };
    liveXRef.current = [];
    liveYRef.current = [];
    liveZRef.current = [];
    liveSpinRef.current = [];
    dirtyRef.current = true;
  }, []);

  // ── Finalize capture and move to next roll or review ─────────────────

  const finalizeCapture = useCallback(() => {
    const cap = captureRef.current;
    if (!cap) return;
    cap.durationMs = performance.now() - cap.startTime;
    captureRef.current = null;

    const updated = [...rollsRef.current];
    const existing = updated.findIndex(r => r.index === cap.index);
    if (existing >= 0) {
      updated[existing] = cap;
    } else {
      updated.push(cap);
    }
    rollsRef.current = updated;
    setRolls(updated);

    if (redoingRef.current) {
      redoingRef.current = false;
      phaseRef.current = 'review';
      setPhase('review');
    } else {
      const nextIdx = cap.index + 1;
      if (nextIdx >= totalRollsRef.current) {
        phaseRef.current = 'review';
        setPhase('review');
      } else {
        rollIndexRef.current = nextIdx;
        setRollIndex(nextIdx);
        phaseRef.current = 'waiting';
        setPhase('waiting');
      }
    }
    setLiveOrient({ x: [], y: [], z: [] });
    setLiveSpin([]);
  }, []);

  // ── Button handler: start/stop recording ─────────────────────────────

  const handleToggleRecording = useCallback(() => {
    if (phaseRef.current === 'waiting') {
      startCapture(rollIndexRef.current);
      phaseRef.current = 'recording';
      setPhase('recording');
    } else if (phaseRef.current === 'recording') {
      finalizeCapture();
    }
  }, [startCapture, finalizeCapture]);

  // ── Event subscriptions (capture data while recording) ───────────────

  useEffect(() => {
    const onEvent = (evt: OddballEvent) => {
      if (phaseRef.current !== 'recording') return;
      const c = captureRef.current;
      if (!c) return;

      if (evt.type === 'cc') {
        const cc = evt as CCUpdateEvent;
        const t = performance.now() - c.startTime;

        c.ccSamples.push({ cc: cc.cc, value: cc.value, t });

        if (cc.cc === OddballCC.OrientX || cc.cc === OddballCC.OrientY || cc.cc === OddballCC.OrientZ) {
          const last = c.orientSamples[c.orientSamples.length - 1];
          const sample = last ? { ...last, t } : { x: 64, y: 64, z: 64, t };
          if (cc.cc === OddballCC.OrientX) sample.x = cc.value;
          if (cc.cc === OddballCC.OrientY) sample.y = cc.value;
          if (cc.cc === OddballCC.OrientZ) sample.z = cc.value;
          c.orientSamples.push(sample);

          liveXRef.current.push(sample.x);
          liveYRef.current.push(sample.y);
          liveZRef.current.push(sample.z);
          dirtyRef.current = true;
        }

        // Capture diagnostics on OrientZ (last in batch)
        if (cc.cc === OddballCC.OrientZ) {
          const d = rollDetector.getDiagnostics();
          c.diagSnapshots.push({
            t,
            rolling: d.rolling,
            backoff: d.backoff,
            impactGrace: d.impactGraceActive,
            energyFast: d.energyFast,
            energySlow: d.energySlow,
            omegaFast: d.omegaFast,
            omegaSlow: d.omegaSlow,
            spinEma: d.spinEma,
            oscillationFreq: d.oscillationFreq,
            dropTimerMs: d.dropTimerMs,
            lowFreqCount: d.lowFreqCount,
          });
        }

        if (cc.cc === OddballCC.SpinSmoothed) {
          c.spinSamples.push({ value: cc.value, t });
          liveSpinRef.current.push(cc.value);
          dirtyRef.current = true;
        }
      }

      if (evt.type === 'tap' || evt.type === 'shake' || evt.type === 'twist') {
        c.notes.push({ type: evt.type, velocity: evt.velocity, t: performance.now() - c.startTime });
      }
    };

    const onRollStart = (evt: RollStartEvent) => {
      const cap = captureRef.current;
      if (!cap || phaseRef.current !== 'recording') return;
      cap.rollStartCount++;
      if (!cap.rollDetected) {
        cap.rollDetected = true;
        cap.detectedAtMs = evt.timestamp - cap.startTime;
      }
    };

    const onRollEnd = (_evt: RollEndEvent) => {
      const cap = captureRef.current;
      if (!cap || phaseRef.current !== 'recording') return;
      cap.rollEndCount++;
      cap.rollEndedAtMs = performance.now() - cap.startTime;
    };

    parser.on('event', onEvent);
    rollDetector.on('rollStart', onRollStart);
    rollDetector.on('rollEnd', onRollEnd);

    // rAF loop for live sparkline updates
    const tick = () => {
      if (dirtyRef.current) {
        dirtyRef.current = false;
        setLiveOrient({
          x: liveXRef.current.slice(-80),
          y: liveYRef.current.slice(-80),
          z: liveZRef.current.slice(-80),
        });
        setLiveSpin(liveSpinRef.current.slice(-80));
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      parser.off('event', onEvent);
      rollDetector.off('rollStart', onRollStart);
      rollDetector.off('rollEnd', onRollEnd);
      cancelAnimationFrame(rafRef.current);
    };
  }, [parser, rollDetector]);

  // ── Actions ──────────────────────────────────────────────────────────

  const handleStart = useCallback((count: number) => {
    totalRollsRef.current = count;
    setTotalRolls(count);
    rollsRef.current = [];
    setRolls([]);
    setRollIndex(0);
    rollIndexRef.current = 0;
    phaseRef.current = 'waiting';
    setPhase('waiting');
  }, []);

  const handleRedo = useCallback((idx: number) => {
    redoingRef.current = true;
    rollIndexRef.current = idx;
    setRollIndex(idx);
    phaseRef.current = 'waiting';
    setPhase('waiting');
    setLiveOrient({ x: [], y: [], z: [] });
    setLiveSpin([]);
  }, []);

  const handleExport = useCallback(() => {
    const payload = {
      exportedAt: new Date().toISOString(),
      rolls: rollsRef.current.sort((a, b) => a.index - b.index),
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'oddball-roll-calibration.json';
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  // ── Render ───────────────────────────────────────────────────────────

  const btn = (label: string, onClick: () => void, accent = false) => (
    <button
      onClick={onClick}
      style={{
        padding: '8px 20px',
        borderRadius: '12px',
        border: `1px solid ${accent ? '#4a7' : '#444'}`,
        background: accent ? '#1a2e1f' : 'transparent',
        color: accent ? '#cfc' : '#aaa',
        cursor: 'pointer',
        fontSize: '0.85rem',
        fontWeight: accent ? 600 : 400,
      }}
    >
      {label}
    </button>
  );

  return (
    <div>
      <h2 style={{ fontSize: '1rem', fontWeight: 300, color: '#888', marginBottom: '12px' }}>
        Roll Calibration
      </h2>

      {/* ── Idle ─────────────────────────────────────────────── */}
      {phase === 'idle' && (
        <div>
          <p style={{ color: '#777', fontSize: '0.8rem', lineHeight: 1.6, maxWidth: 480, marginBottom: 16 }}>
            Capture raw sensor data and gesture detector state during rolls.
            Press Start, then spacebar to start/stop each roll recording.
          </p>
          <div style={{ display: 'flex', gap: 12 }}>
            {btn('Record Single Roll', () => handleStart(1), true)}
            {btn('Record 10 Rolls', () => handleStart(10))}
          </div>
        </div>
      )}

      {/* ── Waiting (press button to begin recording) ────────── */}
      {phase === 'waiting' && (
        <div>
          <div style={{
            padding: '20px',
            background: '#1a1a2a',
            borderRadius: 12,
            textAlign: 'center',
            marginBottom: 16,
          }}>
            <div style={{ fontSize: '1.5rem', color: '#8af', marginBottom: 12 }}>
              Roll #{rollIndex + 1}{totalRolls > 1 ? ` of ${totalRolls}` : ''}
            </div>
            <button
              ref={actionBtnRef}
              onClick={handleToggleRecording}
              style={{
                padding: '12px 32px',
                borderRadius: '14px',
                border: '1px solid #4a7',
                background: '#1a2e1f',
                color: '#cfc',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 600,
              }}
            >
              Start Recording
            </button>
            <div style={{ color: '#666', fontSize: '0.75rem', marginTop: 10 }}>
              Press the button or spacebar, then roll the ball
            </div>
          </div>
        </div>
      )}

      {/* ── Recording (press button to stop) ─────────────────── */}
      {phase === 'recording' && (
        <div>
          <div style={{
            padding: '16px',
            background: '#1a2a1a',
            borderRadius: 12,
            marginBottom: 16,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <div style={{ fontSize: '1.2rem', color: '#4a7' }}>
                Recording Roll #{rollIndex + 1}...
              </div>
              <button
                ref={actionBtnRef}
                onClick={handleToggleRecording}
                style={{
                  padding: '8px 24px',
                  borderRadius: '12px',
                  border: '1px solid #a44',
                  background: '#2a1a1a',
                  color: '#fcc',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                }}
              >
                Stop
              </button>
            </div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: '0.6rem', color: '#666', marginBottom: 4 }}>Orient X</div>
                <Sparkline data={liveOrient.x} color="#f66" />
              </div>
              <div>
                <div style={{ fontSize: '0.6rem', color: '#666', marginBottom: 4 }}>Orient Y</div>
                <Sparkline data={liveOrient.y} color="#6f6" />
              </div>
              <div>
                <div style={{ fontSize: '0.6rem', color: '#666', marginBottom: 4 }}>Orient Z</div>
                <Sparkline data={liveOrient.z} color="#66f" />
              </div>
              <div>
                <div style={{ fontSize: '0.6rem', color: '#666', marginBottom: 4 }}>Spin</div>
                <Sparkline data={liveSpin} color="#fa0" />
              </div>
            </div>
          </div>
          <div style={{ color: '#555', fontSize: '0.7rem' }}>
            Press Stop or spacebar when the roll is finished
          </div>
        </div>
      )}

      {/* ── Review ───────────────────────────────────────────── */}
      {phase === 'review' && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            {btn('Export JSON', handleExport, true)}
            {btn('Record Single', () => handleStart(1))}
            {btn('Record 10', () => handleStart(10))}
          </div>

          {rolls
            .slice()
            .sort((a, b) => a.index - b.index)
            .map((r) => {
              const diag = r.diagSnapshots;
              const total = diag.length;
              const rollingPct = total > 0 ? (diag.filter(d => d.rolling).length / total * 100) : 0;
              const backoffPct = total > 0 ? (diag.filter(d => d.backoff).length / total * 100) : 0;
              const gracePct = total > 0 ? (diag.filter(d => d.impactGrace).length / total * 100) : 0;
              const maxEFast = total > 0 ? Math.max(...diag.map(d => d.energyFast)) : 0;
              const maxESlow = total > 0 ? Math.max(...diag.map(d => d.energySlow)) : 0;

              return (
                <div key={r.index} style={{
                  border: '1px solid #222',
                  borderRadius: 8,
                  padding: '12px 16px',
                  marginBottom: 12,
                  background: '#0d0d0d',
                }}>
                  {/* Header row */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 10 }}>
                    <span style={{ fontSize: '0.9rem', color: '#aaa', fontWeight: 600 }}>
                      Roll #{r.index + 1}
                    </span>
                    <span style={{ fontSize: '0.75rem', color: '#666', fontFamily: 'monospace' }}>
                      {(r.durationMs / 1000).toFixed(1)}s
                    </span>
                    <span style={{ fontSize: '0.75rem', color: '#666', fontFamily: 'monospace' }}>
                      {r.orientSamples.length} samples
                    </span>
                    <span style={{
                      fontSize: '0.65rem', fontFamily: 'monospace', padding: '1px 6px', borderRadius: 4,
                      background: r.rollDetected ? '#1a2e1f' : '#2a1a1a',
                      color: r.rollDetected ? '#4a7' : '#a44',
                    }}>
                      {r.rollDetected ? `detected @ ${Math.round(r.detectedAtMs!)}ms` : 'not detected'}
                    </span>
                    {r.rollStartCount > 1 && (
                      <span style={{
                        fontSize: '0.65rem', fontFamily: 'monospace', padding: '1px 6px', borderRadius: 4,
                        background: '#2a1a0a', color: '#fa6',
                      }}>
                        {r.rollStartCount} starts / {r.rollEndCount} ends
                      </span>
                    )}
                    <button
                      onClick={() => handleRedo(r.index)}
                      style={{
                        marginLeft: 'auto',
                        padding: '2px 10px', borderRadius: 8,
                        border: '1px solid #444', background: 'transparent',
                        color: '#888', cursor: 'pointer', fontSize: '0.65rem',
                      }}
                    >
                      Redo
                    </button>
                  </div>

                  {/* Orient + spin sparklines */}
                  <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ fontSize: '0.55rem', color: '#555', marginBottom: 2 }}>Orient X/Y/Z</div>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <Sparkline data={r.orientSamples.map(s => s.x)} color="#f66" width={80} height={24} />
                        <Sparkline data={r.orientSamples.map(s => s.y)} color="#6f6" width={80} height={24} />
                        <Sparkline data={r.orientSamples.map(s => s.z)} color="#66f" width={80} height={24} />
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.55rem', color: '#555', marginBottom: 2 }}>Spin</div>
                      <Sparkline data={r.spinSamples.map(s => s.value)} color="#fa0" width={80} height={24} />
                    </div>
                  </div>

                  {/* Detector state timelines */}
                  {total > 0 && (
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: '0.6rem', color: '#555', fontWeight: 600, marginBottom: 4 }}>
                        DETECTOR STATE ({total} snapshots)
                      </div>
                      <BoolTimeline data={diag.map(d => d.rolling)} color="#4a7" label={`rolling ${rollingPct.toFixed(0)}%`} />
                      <BoolTimeline data={diag.map(d => d.backoff)} color="#a44" label={`backoff ${backoffPct.toFixed(0)}%`} />
                      <BoolTimeline data={diag.map(d => d.impactGrace)} color="#a74" label={`impact grace ${gracePct.toFixed(0)}%`} />
                    </div>
                  )}

                  {/* Energy / omega sparklines */}
                  {total > 0 && (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <div>
                        <div style={{ fontSize: '0.55rem', color: '#555', marginBottom: 2 }}>
                          E fast (peak {maxEFast.toFixed(0)})
                        </div>
                        <Sparkline data={diag.map(d => d.energyFast)} color="#e85" width={100} height={24} />
                      </div>
                      <div>
                        <div style={{ fontSize: '0.55rem', color: '#555', marginBottom: 2 }}>
                          E slow (peak {maxESlow.toFixed(0)})
                        </div>
                        <Sparkline data={diag.map(d => d.energySlow)} color="#5ae" width={100} height={24} />
                      </div>
                      <div>
                        <div style={{ fontSize: '0.55rem', color: '#555', marginBottom: 2 }}>osc freq</div>
                        <Sparkline data={diag.map(d => d.oscillationFreq)} color="#8d8" width={100} height={24} />
                      </div>
                      <div>
                        <div style={{ fontSize: '0.55rem', color: '#555', marginBottom: 2 }}>drop timer</div>
                        <Sparkline data={diag.map(d => d.dropTimerMs)} color="#a44" width={100} height={24} />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
}
